// envelopeToPanelShape.js
// -----------------------------------------------------------------------------
// Phase 4D — Hover Panel, Chunk 6 (client wiring).
//
// Adapts the server's character-context envelope (defined by
// `server/services/characterContext/CharacterContextComposer.js`, §10 of the
// Chunk-3 resume doc) into the shape `HoverPanelMockup` consumes
// (`mockCharacters.js` schema).
//
// The two shapes are intentionally close — the mockup was designed against the
// envelope during the fold-in session — so this adapter is renames, light
// derivations, and the headline NL string carried over verbatim from the
// server-side templates.
//
// Field map (full-card branch):
//   envelope.affects.top[i].paletteId      → characterContext.dominantAffects[i].emotion
//     (palette key; getPalette() in affectPalette.js accepts canonical
//     bucket ids directly via the Fork-2 unification.)
//   envelope.affects.top[i].share          → dominantAffects[i].share
//   envelope.affects.top[i].charCount      → dominantAffects[i].charCount
//   envelope.affects.top[i].sampleContent  → dominantAffects[i].sampleContent
//   envelope.affects.others.count          → dominantAffects.othersCount (informational)
//   envelope.speakers                      → speakerContext (rename .lineCount→.count)
//   envelope.presence.rows                 → chapterPresence
//   envelope.presence.bookChapterTotal     → bookTotalChapters
//   envelope.phrases.affect                → headlineNL
//   envelope.phrases.speakers              → speakerContextSentence
//   envelope.phrases.presence              → presenceSentence
//   envelope.characterName                 → name
//
// Derived fields (no upstream contract change):
//   roleSize        — "lead" | "supporting role" | "minor role" | "walk-on"
//                     computed from line/chapter totals (lineCount tiers).
//   lineCount       — sum of presence row lineCounts.
//   chapterCount    — count of presence rows with lineCount > 0.
//   voiceExcerpt    — picked from dominantAffects[0].sampleContent if present;
//                     omitted otherwise.
//   bcNote          — null in Chunk 6 (BC overrides land in Chunk 8).
//   isThinCard      — envelope.thinCard verbatim.
//
// Thin-card branch returns the minimal shape `HoverPanelMockup` reads in its
// thin-card path: `isThinCard:true`, `headlineNL` from envelope.phrases.thinCard,
// presence sentence rendered as "" so the mockup skips that section, and the
// dominantAffects/chapterPresence arrays left empty. The mockup explicitly
// guards on `c.isThinCard` for the sparkline and dual-tone resolver.
//
// Pure function; no React, no hooks, no I/O. Safe to import anywhere.
// -----------------------------------------------------------------------------

const DEFAULT_ROLE_SIZE = 'role';

// Heuristic: same tiers used by the design-doc mockup fixtures.
function deriveRoleSize(lineCount) {
  if (!Number.isFinite(lineCount) || lineCount <= 0) return 'walk-on';
  if (lineCount >= 100) return 'lead';
  if (lineCount >= 30) return 'supporting role';
  if (lineCount >= 8) return 'minor role';
  return 'walk-on';
}

// Strip a single surrounding pair of straight or curly quotes for excerpt
// display. Mirrors the existing HoverPanelMockup quote-strip pattern but
// applied at adapter time so the panel's prop has the cleaner string.
function trimSurroundingQuotes(s) {
  if (typeof s !== 'string') return '';
  return s.replace(/^["\u201C]/, '').replace(/["\u201D]$/, '').trim();
}

// Hard cap on the "In their own words" blockquote length. Operator-set:
// long dialogue lines from the corpus (and the narration-snippet fallback)
// can run several hundred characters and crowd the panel. 500 chars is the
// readability ceiling; beyond that we ellipsise.
const VOICE_EXCERPT_MAX_LEN = 500;

// Truncate at maxLen-1 chars (reserving one position for the ellipsis), then
// back off to the nearest preceding whitespace within a 40-char window so we
// don't slice a word in half. Trailing punctuation/whitespace is stripped
// before the ellipsis is appended for typographically clean "…end of phrase…"
// rather than "…end of phrase ,…".
//
// The final string length is at most maxLen characters including the U+2026
// horizontal-ellipsis glyph (single character). Strings shorter than the cap
// pass through unchanged.
function truncateExcerpt(s, maxLen = VOICE_EXCERPT_MAX_LEN) {
  if (typeof s !== 'string') return '';
  if (s.length <= maxLen) return s;

  const ELLIPSIS = '\u2026';
  const window = 40;
  const hardCut = s.slice(0, maxLen - 1); // leave room for the ellipsis
  const wsIdx = hardCut.lastIndexOf(' ');
  const cut =
    wsIdx >= (maxLen - 1) - window
      ? hardCut.slice(0, wsIdx)
      : hardCut;

  return cut.replace(/[\s,.;:\u2014\-]+$/u, '') + ELLIPSIS;
}

/**
 * Convert a server envelope into the `characterContext` prop shape consumed
 * by HoverPanelMockup.
 *
 * @param {object} envelope  The `data` field of the controller response.
 *                            Either the full-card or thin-card branch.
 * @param {object} [extras]
 * @param {string} [extras.bookTitle]      Overlay shown under the role line.
 * @returns {object}  characterContext (always has `.name` + `.isThinCard`).
 */
export function envelopeToPanelShape(envelope, extras = {}) {
  if (!envelope || typeof envelope !== 'object') {
    return null;
  }

  const bookTitle = extras.bookTitle || null;
  const name = envelope.characterName || '';

  // -------------------------------------------------------------------------
  // Thin-card branch — the server emits this for zero-data / book-not-found.
  // -------------------------------------------------------------------------
  if (envelope.thinCard) {
    return {
      id: envelope.characterUuid || null,
      name,
      roleSize: 'walk-on',
      lineCount: 0,
      chapterCount: 0,
      bookTotalChapters: 0,
      headlineNL: (envelope.phrases && envelope.phrases.thinCard) || '',
      speakerContextSentence: null,
      presenceSentence: null,
      voiceExcerpt: null,
      dominantAffects: [],
      speakerContext: [],
      chapterPresence: [],
      bcNote: null,
      isThinCard: true,
      ...(bookTitle ? { bookTitle } : {}),
      // Diagnostic — never read by the mockup, useful for debugging.
      __envelopeVersion: envelope.v || null
    };
  }

  // -------------------------------------------------------------------------
  // Full-card branch.
  // -------------------------------------------------------------------------
  const affectsTop = Array.isArray(envelope.affects && envelope.affects.top)
    ? envelope.affects.top
    : [];

  const dominantAffects = affectsTop.map((row) => ({
    // `emotion` is the string the pill row prints AND the palette-lookup
    // key for `affectPalette.getPalette()`. The server keeps two strings
    // per bucket (see `server/services/characterContext/affectVocabulary.js`
    // PALETTE_BY_ID): `paletteId` is the system identifier
    // ("positive", "negative", "directive", "intense", …) and `label` is
    // the prose word the NL templates print ("supportive", "irritated",
    // "strategic", "frustrated", …). Six of the nine buckets have
    // diverging strings.
    //
    // The headline NL renders `label`; the pill row used to render
    // `paletteId`, producing a same-six-inches mismatch — "Mostly
    // supportive and …" with a "positive · 42%" chip right under it,
    // which read as "is this the same thing or another thing?" to VAs
    // skimming the panel. Operator decision 2026-05-19: prefer `label`
    // on the pill row so both surfaces speak the same vocabulary.
    //
    // The palette-lookup contract is preserved by `getPalette`'s Fork-2
    // unification: the LEGACY_LABEL_TO_BUCKET map (in
    // `client/src/components/characterContext/HoverPanelMockup/affectPalette.js`)
    // already routes the prose words ("supportive", "irritated",
    // "strategic", "teasing", "frustrated", "surprised", "varied") to
    // their canonical bucket entries. The neutral-demotion rule in
    // `pickDrivingAffect` / `resolveDualTone` also still triggers
    // correctly because the neutral bucket's `label` is the string
    // `'neutral'`. So this swap is pure-surface — no palette/motif
    // breakage, no envelope-shape churn.
    emotion: row.label || row.paletteId || 'neutral',
    label: row.label || row.paletteId || 'neutral',
    paletteId: row.paletteId || row.label || 'neutral',
    charCount: Number.isFinite(row.charCount) ? row.charCount : 0,
    share: Number.isFinite(row.share) ? row.share : 0,
    sampleContent: row.sampleContent || ''
  }));

  // Optional "and N more shades" indicator. The mockup doesn't render this
  // explicitly yet; the affect template phrase already mentions the "N other
  // shades" tail. Carried through so a future iteration can surface it.
  const othersCount =
    envelope.affects && envelope.affects.others
      ? envelope.affects.others.count || 0
      : 0;

  // Speaker rows: rename .lineCount → .count to match the mockup's existing
  // SpeakersBars contract. `.share` already matches.
  const speakers = Array.isArray(envelope.speakers) ? envelope.speakers : [];
  const speakerContext = speakers.map((row) => ({
    name: row.name || '',
    count: Number.isFinite(row.lineCount) ? row.lineCount : 0,
    share: Number.isFinite(row.share) ? row.share : 0
  }));

  // Presence rows pass through 1:1 — the field names already match
  // (`chapter`, `lineCount`, `sampleLines: [{ line, toName, role }]`).
  //
  // `role` ('response' | 'initiation' | null) was added server-side
  // 2026-05-19 with the KI-1 resolver rewrite. The mockup does not read
  // it yet, but we forward it through here so a future styling pass can
  // surface the response/initiation distinction without another adapter
  // change. `toName === null` (true soliloquy / chapter-of-narration)
  // already suppresses the "— to X" attribution line via the existing
  // `{l.toName && (…)}` guard in HoverPanelMockup.jsx.
  const presenceRows =
    envelope.presence && Array.isArray(envelope.presence.rows)
      ? envelope.presence.rows
      : [];
  const chapterPresence = presenceRows.map((row) => ({
    chapter: row.chapter,
    lineCount: Number.isFinite(row.lineCount) ? row.lineCount : 0,
    sampleLines: Array.isArray(row.sampleLines) ? row.sampleLines : []
  }));

  const bookTotalChapters =
    envelope.presence && Number.isFinite(envelope.presence.bookChapterTotal)
      ? envelope.presence.bookChapterTotal
      : 0;

  // Aggregates for the header strip.
  const lineCount = chapterPresence.reduce((sum, r) => sum + (r.lineCount || 0), 0);
  const chapterCount = chapterPresence.filter((r) => (r.lineCount || 0) > 0).length;
  const roleSize = deriveRoleSize(lineCount);

  // Voice excerpt for the "In their own words" blockquote.
  //
  // Source-of-truth ranking (most-to-least preferred):
  //
  //   1. The character's actual DialogueLines from `presence.rows[].sampleLines[]`.
  //      These come from `:DialogueLine.content` for the chapter's top-3 lines
  //      by length (Chunk-2 RolePresenceService rule). They are by definition
  //      things this character says, so they make accurate "in their own words"
  //      pulls. We pick the longest sample line across all chapters with a
  //      lex-asc tie-break for determinism.
  //
  //   2. Fallback: `dominantAffects[0].sampleContent` — the longest CSS-attached
  //      TextSnippet for the character's top affect bucket. In the prototype
  //      corpus these TextSnippets are sometimes setup/narration snippets that
  //      multiple characters end up sharing (because the first paragraph of the
  //      book is dragged in as character-context background). So this is the
  //      fallback, not the primary, when DialogueLine samples are present.
  //
  // The mockup expects `{ emotion, content }`. `emotion` is the human-readable
  // top-bucket label so the small "In their own words · {emotion}" caption
  // still reads naturally ("frustrated" not the bucket id "intense").
  // Driving-affect demotion for the excerpt caption (operator decision
  // 2026-05-19): when dominantAffects[0] is `neutral` and a second row
  // exists, the small "In their own words · _____" caption uses the
  // second row's label. Mirrors the server-side rule in
  // `nlTemplates.renderAffectPhrase` and the client-side rule in
  // `affectPalette.pickDrivingAffect` so the prose, the caption, and the
  // palette/motif all name the same flavour. The pill row is unchanged
  // — neutral still ranks first there (factual surface).
  let voiceExcerpt = null;
  const captionPick = (() => {
    if (dominantAffects.length === 0) return null;
    if (
      dominantAffects[0].emotion === 'neutral' &&
      dominantAffects.length > 1
    ) {
      return dominantAffects[1];
    }
    return dominantAffects[0];
  })();
  const excerptEmotion =
    (captionPick && (captionPick.label || captionPick.emotion)) || 'voice';

  // Walk all chapter sample-line entries and find the longest .line string.
  let bestDialogueLine = '';
  for (const chapter of chapterPresence) {
    const lines = Array.isArray(chapter.sampleLines) ? chapter.sampleLines : [];
    for (const sl of lines) {
      const candidate = (sl && typeof sl.line === 'string') ? sl.line.trim() : '';
      if (!candidate) continue;
      if (
        candidate.length > bestDialogueLine.length ||
        (candidate.length === bestDialogueLine.length && candidate < bestDialogueLine)
      ) {
        bestDialogueLine = candidate;
      }
    }
  }

  if (bestDialogueLine) {
    voiceExcerpt = {
      emotion: excerptEmotion,
      content: truncateExcerpt(trimSurroundingQuotes(bestDialogueLine))
    };
  } else if (dominantAffects.length > 0 && dominantAffects[0].sampleContent) {
    voiceExcerpt = {
      emotion: excerptEmotion,
      content: truncateExcerpt(trimSurroundingQuotes(dominantAffects[0].sampleContent))
    };
  }

  return {
    id: envelope.characterUuid || null,
    name,
    roleSize: roleSize || DEFAULT_ROLE_SIZE,
    lineCount,
    chapterCount,
    bookTotalChapters,

    headlineNL: (envelope.phrases && envelope.phrases.affect) || '',
    speakerContextSentence:
      (envelope.phrases && envelope.phrases.speakers) || null,
    presenceSentence:
      (envelope.phrases && envelope.phrases.presence) || null,

    voiceExcerpt,
    dominantAffects,
    speakerContext,
    chapterPresence,

    // BC override copy lands in Chunk 8; null until then.
    bcNote: null,

    isThinCard: false,

    ...(bookTitle ? { bookTitle } : {}),

    // Diagnostic — never read by the mockup.
    __envelopeVersion: envelope.v || null,
    __othersCount: othersCount,
    __generatedAt: envelope.generatedAt || null
  };
}

export default envelopeToPanelShape;
